import {
  getItemSpreadPosition,
  isShallowEqual,
  type Manifest,
  type SpreadPosition,
} from "@prose-reader/shared"
import {
  filter,
  first,
  map,
  merge,
  type Observable,
  type ObservedValueOf,
  of,
  Subject,
  share,
  switchMap,
} from "rxjs"
import type { Context } from "../context/Context"
import type { HookManager } from "../hooks/HookManager"
import { isFullyPrePaginated } from "../manifest/isFullyPrePaginated"
import type { ReaderSettingsManager } from "../settings/ReaderSettingsManager"
import { DestroyableClass } from "../utils/DestroyableClass"
import {
  removeStylePropertyIfPresent,
  setStylePropertyIfChanged,
} from "../utils/dom"
import { deferNextResult } from "../utils/rxjs"
import type { Viewport } from "../viewport/Viewport"
import type { DocumentRenderer } from "./renderer/DocumentRenderer"

export class SpineItemLayout extends DestroyableClass {
  private layoutTriggerSubject = new Subject<{
    spreadPosition: SpreadPosition
    horizontalOffset: number
    isLastItem: boolean
    edgeX: number
    edgeY: number
  }>()

  private lastLayout: {
    width: number
    height: number
    pageSize: { width: number; height: number }
  } | null = null

  public readonly didLayout$: Observable<{ width: number; height: number }>

  constructor(
    public item: Manifest[`spineItems`][number],
    public containerElement: HTMLElement,
    public context: Context,
    public hookManager: HookManager,
    public renderer: DocumentRenderer,
    public settings: ReaderSettingsManager,
    public viewport: Viewport,
  ) {
    super()

    const layoutProcess$ = this.layoutTriggerSubject.pipe(
      switchMap(
        ({ spreadPosition, horizontalOffset, isLastItem, edgeX, edgeY }) => {
          const { blankPagePosition, minimumWidth } =
            this.computeLayoutInformation({
              horizontalOffset,
              isLastItem,
              spreadPosition,
            })

          this.hookManager.execute(`item.onBeforeLayout`, {
            blankPagePosition,
            item: this.item,
            minimumWidth,
          })

          const rendererLayout$ = this.renderer.layout({
            blankPagePosition,
            minPageSpread: minimumWidth / this.viewport.pageSize.width,
            minimumWidth,
            spreadPosition,
          })

          return merge(
            of({ type: "start" } as const),
            rendererLayout$.pipe(
              /**
               * Now that inner layout is done from the renderer, we adjust the dimensions
               * of the outer container element
               */
              this.updateContainerLayout({
                minimumWidth,
                edgeX,
                edgeY,
              }),
              map((data) => {
                this.hookManager.execute(`item.onAfterLayout`, {
                  blankPagePosition,
                  item: this.item,
                  minimumWidth,
                })

                return {
                  type: "end",
                  data,
                } as const
              }),
            ),
          )
        },
      ),
      share(),
    )

    this.didLayout$ = layoutProcess$.pipe(
      filter((event) => event.type === `end`),
      map((event) => event.data),
      share(),
    )
  }

  private validateDimension(
    value: number,
    pageSize: number,
    minimum: number,
  ): number {
    if (value <= 0) return minimum

    const maxValue = Math.max(value, minimum)

    // Check if value is a multiple of page size
    const multiplier = Math.ceil(maxValue / pageSize)
    const adjustedValue = multiplier * pageSize

    // Ensure the value meets minimum requirement
    return Math.max(adjustedValue, pageSize)
  }

  private computeLayoutInformation = ({
    isLastItem,
    horizontalOffset,
    spreadPosition,
  }: {
    isLastItem: boolean
    horizontalOffset: number
    spreadPosition: SpreadPosition
  }) => {
    let minimumWidth = this.viewport.value.pageSize.width
    let blankPagePosition: `none` | `before` | `after` = `none`
    const isScreenStartItem =
      horizontalOffset % this.viewport.absoluteViewport.width === 0
    const manifest = this.context.manifest
    const isGloballyPrePaginated = isFullyPrePaginated(manifest)

    if (this.settings.values.computedSpreadMode) {
      /**
       * for now every reflowable content that has reflow siblings takes the entire screen by default
       * this simplify many things and I am not sure the specs allow one reflow
       * to end and an other one to start on the same screen anyway
       *
       * @important
       * For now this is impossible to have reflow not taking all screen. This is because
       * when an element is unloaded, the next element will move back its x axis, then an adjustment
       * will occurs and the previous element will become visible again, meaning it will be loaded,
       * therefore pushing the focused element, meaning adjustment again, then unload of previous one,
       * ... infinite loop. Due to the nature of reflow it's pretty much impossible to not load the entire
       * book with spread on to make it work.
       *
       * @important
       * When the book is globally pre-paginated we will not apply any of this even if each item is
       * reflowable. This is mostly a publisher mistake but does not comply with spec. Therefore
       * we ignore it
       */
      if (
        !isGloballyPrePaginated &&
        this.renderer.renditionLayout === `reflowable` &&
        !isLastItem
      ) {
        minimumWidth = this.viewport.value.pageSize.width * 2
      }

      // mainly to make loading screen looks good
      if (
        !isGloballyPrePaginated &&
        this.renderer.renditionLayout === `reflowable` &&
        isLastItem &&
        isScreenStartItem
      ) {
        minimumWidth = this.viewport.value.pageSize.width * 2
      }

      const lastItemStartOnNewScreenInAPrepaginatedBook =
        isScreenStartItem && isLastItem && isGloballyPrePaginated
      const requestedSpreadPosition = getItemSpreadPosition(this.item)

      if (
        requestedSpreadPosition !== undefined &&
        requestedSpreadPosition !== spreadPosition
      ) {
        blankPagePosition = `before`
        minimumWidth = this.viewport.value.pageSize.width * 2
      } else if (lastItemStartOnNewScreenInAPrepaginatedBook) {
        if (this.context.isRTL()) {
          blankPagePosition = `before`
        } else {
          blankPagePosition = `after`
        }

        minimumWidth = this.viewport.value.pageSize.width * 2
      }
    }

    return {
      minimumWidth,
      blankPagePosition,
    }
  }

  private adjustPositionOfElement = ({
    right,
    left,
    top,
  }: {
    right?: number
    left?: number
    top?: number
  }) => {
    if (right !== undefined) {
      setStylePropertyIfChanged(
        this.containerElement.style,
        `right`,
        `${right}px`,
      )
    } else {
      removeStylePropertyIfPresent(this.containerElement.style, `right`)
    }
    if (left !== undefined) {
      setStylePropertyIfChanged(
        this.containerElement.style,
        `left`,
        `${left}px`,
      )
    } else {
      removeStylePropertyIfPresent(this.containerElement.style, `left`)
    }
    if (top !== undefined) {
      setStylePropertyIfChanged(this.containerElement.style, `top`, `${top}px`)
    } else {
      removeStylePropertyIfPresent(this.containerElement.style, `top`)
    }
  }

  private updateContainerLayout =
    ({
      minimumWidth,
      edgeX,
      edgeY,
    }: {
      minimumWidth: number
      edgeX: number
      edgeY: number
    }) =>
    (stream: Observable<{ width: number; height: number } | undefined>) =>
      stream.pipe(
        map((dims) => {
          const trustableLastLayout = isShallowEqual(
            this.lastLayout?.pageSize,
            this.viewport.pageSize,
          )
            ? this.lastLayout
            : undefined

          const { width: previousWidth, height: previousHeight } =
            trustableLastLayout ?? {}
          const { width = previousWidth, height = previousHeight } = dims ?? {}
          const { width: pageSizeWidth, height: pageSizeHeight } =
            this.viewport.pageSize

          const safeWidth = this.validateDimension(
            width ?? pageSizeWidth,
            pageSizeWidth,
            minimumWidth,
          )
          const safeHeight =
            this.settings.values.computedPageTurnMode === "scrollable"
              ? (height ?? pageSizeHeight)
              : this.validateDimension(
                  height ?? pageSizeHeight,
                  pageSizeHeight,
                  pageSizeHeight,
                )

          this.lastLayout = {
            width: safeWidth,
            height: safeHeight,
            pageSize: this.viewport.pageSize,
          }

          setStylePropertyIfChanged(
            this.containerElement.style,
            `width`,
            `${safeWidth}px`,
          )
          setStylePropertyIfChanged(
            this.containerElement.style,
            `height`,
            `${safeHeight}px`,
          )

          if (this.settings.values.computedPageTurnDirection === `vertical`) {
            this.adjustPositionOfElement({
              top: edgeY,
              left: edgeX,
            })

            return { width: safeWidth, height: safeHeight }
          }

          // We can now adjust the position of the item if needed based on its new layout.
          // For simplification we use an edge offset, which means for LTR it will be x from left and for RTL
          // it will be x from right
          this.adjustPositionOfElement(
            this.context.isRTL()
              ? { right: edgeX, top: 0 }
              : { left: edgeX, top: 0 },
          )

          return { width: safeWidth, height: safeHeight }
        }),
      )

  public layout = (
    params: ObservedValueOf<typeof this.layoutTriggerSubject>,
  ) => {
    const nextResult = deferNextResult(this.didLayout$.pipe(first()))

    this.layoutTriggerSubject.next(params)

    return nextResult()
  }

  get layoutInfo() {
    const style = this.containerElement.style
    const width = style.width ? parseFloat(style.width) : 0
    const height = style.height ? parseFloat(style.height) : 0

    return {
      width,
      height,
    }
  }
}
